import React, { useMemo, useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import { closeHelpCenter } from '../../store/slices/helpSlice';
import { HELP_CATEGORIES, HELP_TOPICS } from '../../lib/help/helpTopics';
import { trackHelpEvent } from '../../lib/help/helpAnalytics';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
} from '../ui/Dialog';
import { useTranslation } from 'react-i18next';

const HelpCenterModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const tr = (key: string, defaultValue: string, options?: Record<string, unknown>): string => {
    const translated = t(key, { ...(options ?? {}), defaultValue });
    return translated === key ? defaultValue : translated;
  };
  const open = useAppSelector((state) => state.help.isHelpCenterOpen);
  const context = useAppSelector((state) => state.help.lastHelpContext);
  const [activeCategory, setActiveCategory] = useState<(typeof HELP_CATEGORIES)[number]['id']>('getting_started');
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    trackHelpEvent('help_center_opened', { context, source: 'help_center_modal' });
  }, [open, context]);

  const filteredTopics = useMemo(() => {
    const lowered = query.trim().toLowerCase();
    return HELP_TOPICS.filter((topic) => {
      const byCategory = topic.category === activeCategory;
      if (!byCategory) return false;
      if (!lowered) return true;
      return (
        topic.title.toLowerCase().includes(lowered) ||
        topic.summary.toLowerCase().includes(lowered) ||
        topic.body.some((line) => line.toLowerCase().includes(lowered))
      );
    });
  }, [activeCategory, query]);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) dispatch(closeHelpCenter());
      }}
    >
      <DialogContent size="responsive-full">
        <DialogHeader>
          <DialogTitle>{tr('help.title', 'Help Center')}</DialogTitle>
          <DialogDescription>
            {tr('help.contextDescription', `Context: ${context}. Find quick guidance, workflows, and support links.`, {
              context,
            })}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
            <aside className="space-y-2">
              <label className="sr-only" htmlFor="help-search">
                {tr('help.searchTopicsLabel', 'Search help topics')}
              </label>
              <input
                id="help-search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="w-full rounded-lg border border-border-subtle bg-surface-container px-3 py-2 text-sm text-text-primary"
                placeholder={tr('help.searchPlaceholder', 'Search help...')}
              />

              <nav
                className="space-y-1"
                aria-label={tr('help.categoriesLabel', 'Help categories')}
              >
                {HELP_CATEGORIES.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => {
                      setActiveCategory(category.id);
                      trackHelpEvent('help_topic_viewed', {
                        context,
                        category: category.id,
                        source: 'category_tab',
                      });
                    }}
                    className={`w-full rounded-md px-3 py-2 text-left text-sm ${
                      activeCategory === category.id
                        ? 'bg-accent-primary/20 text-accent-primary'
                        : 'text-text-secondary hover:bg-surface-container-low'
                    }`}
                  >
                    {category.label}
                  </button>
                ))}
              </nav>

            </aside>

            <section className="space-y-3">
              {filteredTopics.length === 0 ? (
                <div className="rounded-lg border border-border-subtle bg-surface-container-low p-4 text-sm text-text-secondary">
                  {tr('help.noTopics', 'No topics match your search in this category.')}
                </div>
              ) : (
                filteredTopics.map((topic) => (
                  <article
                    key={topic.id}
                    className="rounded-lg border border-border-subtle bg-surface-container-low p-4"
                  >
                    <h3 className="mb-1 text-base font-semibold text-text-primary">{topic.title}</h3>
                    <p className="mb-3 text-sm text-text-secondary">{topic.summary}</p>
                    <ul className="list-disc space-y-1 pl-5 text-sm text-text-secondary">
                      {topic.body.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                    {topic.quickActions?.length ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {topic.quickActions.map((action) =>
                          action.href ? (
                            <button
                              key={action.label}
                              type="button"
                              className="rounded-md bg-surface-container px-3 py-1.5 text-xs text-text-primary hover:bg-surface-container-high"
                              onClick={() => {
                                window.open(action.href, '_blank', 'noopener,noreferrer');
                                trackHelpEvent('external_help_opened', {
                                  context,
                                  source: 'topic_action',
                                  topicId: topic.id,
                                });
                              }}
                            >
                              {action.label}
                            </button>
                          ) : null
                        )}
                      </div>
                    ) : null}
                  </article>
                ))
              )}
            </section>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export default HelpCenterModal;
